import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon, ZoomInIcon } from 'lucide-react';
import type { CaseStudyImage } from '../data/amexCaseStudy';
import { Lightbox } from './Lightbox';
type ImageCarouselProps = {
  images: CaseStudyImage[];
  background: string;
};
const arrowClass = 'flex h-[37px] w-[37px] items-center justify-center rounded-full border border-white text-white transition-[background-color,opacity] duration-150 hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent';
export function ImageCarousel({
  images,
  background
}: ImageCarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    updateArrows();
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, [updateArrows]);
  const scrollByItem = (direction: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const item = el.querySelector<HTMLElement>('[data-carousel-item]');
    const step = item ? item.offsetWidth + 18 : el.clientWidth * 0.8;
    el.scrollBy({
      left: step * direction,
      behavior: 'smooth'
    });
  };
  return <section aria-label="Project screenshots" className="w-full" style={{
    background
  }} data-id="element-92" data-mp="sgxdqxr3gpqk">
      <div ref={trackRef} onScroll={updateArrows} className="flex snap-x snap-mandatory items-start gap-[18px] overflow-x-auto scroll-px-[30px] px-[30px] pb-5 pt-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-93" data-mp="2z75b7ggu00jx">
        {images.map((image, i) => <button key={image.src} type="button" data-carousel-item onClick={() => setOpenIndex(i)} aria-label={`Enlarge screenshot ${i + 1}: ${image.alt}`} className="group relative w-[78%] shrink-0 snap-start rounded-[5px] p-2 focus-visible:outline-none sm:w-[44%] lg:w-[calc((100%-54px)/4)]" data-id="element-94" data-mp="y78qlhszvmtz" data-mpi={i}>
            <span className="relative block rounded-[5px] shadow-[0px_4px_4px_rgba(0,0,0,0.25)] transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:scale-[1.03] group-focus-visible:scale-[1.03] group-focus-visible:outline group-focus-visible:outline-2 group-focus-visible:outline-offset-2 group-focus-visible:outline-white motion-reduce:transform-none" data-id="element-95" data-mp="1h3i2bygfc1at">
              <img src={image.src} alt="" className="block h-auto w-full rounded-[5px]" draggable={false} data-id="element-96" data-mp="1zvucv8h8e8w1" />
              <span className="pointer-events-none absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-visible:opacity-100" data-id="element-97" data-mp="24tm37y289my4">
                <ZoomInIcon className="h-[18px] w-[18px]" aria-hidden="true" data-id="element-98" data-mp="3g9x5cycpk8wv" />
              </span>
            </span>
          </button>)}
      </div>

      <div className="flex justify-center gap-[10px] pb-5" data-id="element-99" data-mp="3bdyk3jmof70o">
        <button type="button" aria-label="Previous screenshots" className={arrowClass} disabled={!canPrev} onClick={() => scrollByItem(-1)} data-id="element-100" data-mp="bv73x3slopfz">
          <ArrowLeftIcon className="h-[18px] w-[18px]" strokeWidth={2} data-id="element-101" data-mp="7x9fi36nm7dn" />
        </button>
        <button type="button" aria-label="Next screenshots" className={arrowClass} disabled={!canNext} onClick={() => scrollByItem(1)} data-id="element-102" data-mp="322hnc02fwwn">
          <ArrowRightIcon className="h-[18px] w-[18px]" strokeWidth={2} data-id="element-103" data-mp="vkzou3wzkyy5" />
        </button>
      </div>

      <Lightbox images={images} index={openIndex} onClose={() => setOpenIndex(null)} onChange={setOpenIndex} data-id="element-104" data-mp="qt7x3q23gn6f" />
    </section>;
}